import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { BodyCompositionDataChangedEvent, UserDb } from 'services/user-db.service';
import { PubSubService } from 'services/pubsub.service';
import { AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { CoachResolvedAction, Log_WeeklyCheckup, readPositiveCalories, readProgressSnapshot } from '@cf-shared/data-model/log-weekly-checkup';
import { DebugService } from 'services/debug.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { FitnessState } from '@cf-shared/data-model/user-state';
import { LoadingController } from 'providers/loading-controller';
import { TelemetryService } from 'services/telemetry.service';
import { deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { CsInputMeasureComponent } from 'components/cs-input-measure/cs-input-measure';
import { DestroyGuard } from 'services/browser-utils';
import { ClientNotifyCheckupResolvedParams } from '@cf-shared/coach-notify-interface';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ToastController } from 'providers/toast-controller';
import { CalorieChangeDirection } from 'components/cs-calorie-change-input/calorie-change';
import { CalorieChangeAudience, CsCalorieChangeInputComponent } from 'components/cs-calorie-change-input/cs-calorie-change-input';
import { BodyCompOverviewDataService, completedCheckupOverviewRequest } from 'services/body-comp-overview-data.service';
import { BodyCompOverviewModel, OverviewMeasurement, weightChangeMeasurement } from 'services/body-comp-overview-model';
import { SettingsService } from 'services/settings.service';
import { getMeasureUnitConversion } from 'util/formatting';
import {
  CoachReviewValidationError,
  applyCoachResolutionToCheckup,
  applyCustomCaloriesToPendingPlan,
  getCoachResolutionOutcome,
  hasUnsavedCoachCalorieTarget,
  keepLiveTrainingProgram,
} from './weekly-checkup-coach-resolution';
import { TrainingOverviewRowTap } from 'components/cs-training-overview/cs-training-overview';
import { ITrainingOverviewPage } from 'pages/training-overview/training-overview-interface';
import { NavController } from 'providers/nav-controller';
import { TrainingOverviewModel } from 'services/training-overview';
import { TrainingOverviewActionsService } from 'services/training-overview-actions.service';
import { TrainingOverviewDataService } from 'services/training-overview-data.service';
import { TrainingOverviewPaging, TrainingOverviewPagingAnswer, TrainingOverviewPagingButtons } from 'services/training-overview-paging';

/** the two halves of the coach's screen while the coaching dashboard switch is on (#5456) */
export enum CoachReviewTab {
  /** the summary card and the three answers */
  Decision = 'Decision',
  /** the client's Body Composition overview for this check-up */
  BodyComposition = 'BodyComposition',
  /** the client's Training Overview, up to today (phase 3) */
  Training = 'Training',
}

/**
 * The screen a coach sees when they open a weekly check-up their client has submitted.
 *
 * A coached client fills in the check-up and, when the app recommends a change to their
 * daily calorie target, sends it to their coach rather than accepting it themselves. The
 * coach then sees this screen: a card with the target in force today and the one the app
 * recommends, and three buttons — Decline, Modify, Accept. Modify opens a field seeded
 * with the recommendation so the coach can put a different number on it.
 *
 * While the coaching dashboard switch is on (#5456) the screen has the tabs Decision, Body
 * Composition and Training; the first figure is labelled as what it is, the intake the client
 * reported; and Modify takes the target as kcal or as a percent of that intake. With the switch
 * off the screen is what it was. See design-docs/coaching-dashboard.md, "The coach's review".
 *
 * This draws the WHOLE screen — its own header, its own content and its own button bar —
 * rather than a card inside the check-up page. That is why it exists at all: the coach's
 * screen and the client's six-step wizard share no state, and keeping both in one page
 * class meant every change to either was made by first working out which of the two it
 * was allowed to touch. Split out of `weekly-checkup-page.ts` by
 * https://github.com/cybsol/mh-physique/issues/5274.
 *
 * Two things deliberately stay with the parent page and are not done here:
 *
 * - **Closing the page.** When the coach has answered, this emits `resolved` and the page
 *   does the rest. The page has to both mark the check-up as needing no "throw your
 *   answers away?" question AND close itself, and those two belong together — a child
 *   that closed the page on its own would put that dialog in front of a coach who had
 *   just finished.
 * - **The document watcher.** The page creates it, and this component is handed it. Its
 *   own writes have to be able to read the plan back after the check-up is saved, and the
 *   page outlives every one of those reads.
 */
@Component({
  selector: 'cs-coach-checkup-review',
  templateUrl: 'cs-coach-checkup-review.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'coach-checkup-review'
  }
})
export class CsCoachCheckupReview implements OnDestroy {
  protected LL = LL;

  /** the check-up the coach is answering — loaded by the page's guard before this is shown */
  @Input() checkup: Log_WeeklyCheckup;

  /** the page's watcher, used for the plan and fitness-state reads while applying the answer */
  @Input() docWatcher: DocWatcher;

  /**
   * The coaching dashboard switch, read by the page for the signed-in account (the coach). When it
   * turns off while the review is open, the tabs go away, so the Decision content is brought forward;
   * otherwise it would stay hidden behind a tab that no longer exists.
   */
  @Input() set coachingDashboardOn(isOn: boolean) {
    this._coachingDashboardOn = isOn;
    if (!isOn) this.tab = CoachReviewTab.Decision;
  }
  get coachingDashboardOn(): boolean {
    return this._coachingDashboardOn;
  }
  private _coachingDashboardOn = false;

  /**
   * The name of the PAGE's method that receives the result of Exercise Settings opened from the
   * Training tab's menu. A return callback is resolved on the page, never on this component.
   */
  @Input() trainingSettingsCallback = '';

  /**
   * The coach has answered and everything is written. The page closes itself, without
   * asking about unsaved changes.
   */
  @Output() resolved = new EventEmitter<void>();

  /** which half of the screen is showing: the summary, or the typed-target field */
  protected reviewMode: 'summary' | 'modify' = 'summary';

  /** the target the coach has typed; only written to the plan when they press Apply. undefined while the field is empty */
  protected customCalories: number | undefined;

  /** the answer picked on the Decision tab while the switch is on (#5456); undefined until the coach picks one */
  protected decision: CoachResolvedAction | undefined;
  /** the answers the Decision tab lists, in order */
  protected readonly decisionChoices = [CoachResolvedAction.Accepted, CoachResolvedAction.Modified, CoachResolvedAction.Declined];
  protected CoachResolvedAction = CoachResolvedAction;

  /** true from the moment a button is pressed until the answer is written or has failed */
  protected isInFlight = false;

  @ViewChild('coachModifyInput', { static: false }) protected modifyInput?: CsInputMeasureComponent;
  /** the kcal-or-percent control that replaces the single field while the switch is on */
  @ViewChild('coachCalorieChange', { static: false }) protected calorieChangeInput?: CsCalorieChangeInputComponent;

  protected CoachReviewTab = CoachReviewTab;
  protected CalorieChangeAudience = CalorieChangeAudience;
  /** which tab shows; Decision on open */
  protected tab = CoachReviewTab.Decision;

  // the Body Composition tab: loaded the first time it is opened, never before, so the Decision
  // tab opens as fast as it did without it
  protected overviewModel?: BodyCompOverviewModel;
  protected overviewLoading = false;
  protected overviewFailed = false;
  /** true once the tab was opened; its content is then kept in the page, only hidden */
  protected overviewRequested = false;

  // the Training tab (phase 3): the client's Training Overview up to today, loaded the first time
  // the tab is opened and again whenever the page says the program may have changed
  protected trainingModel?: TrainingOverviewModel;
  protected trainingLoading = false;
  protected trainingFailed = false;
  /** which sessions each workout card of the Training tab shows; pages the same way as the Training Overview page (#6676) */
  protected trainingPaging = new TrainingOverviewPaging();
  /** the paging buttons of the shown Training tab model, set together with it */
  protected trainingButtons: TrainingOverviewPagingButtons = { canShowEarlier: [], canShowLater: [] };
  /** true once the tab was opened; its content is then kept in the page, only hidden */
  protected trainingRequested = false;
  /** counts the tab's loads, so an answer that arrives after a newer request is dropped */
  private trainingLoadCounter = 0;

  private destroyGuard = new DestroyGuard();

  constructor(
    private userDb: UserDb,
    private auth: AuthService,
    private logging: LoggingService,
    private debug: DebugService,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private loadingCtrl: LoadingController,
    private telemetry: TelemetryService,
    private pubSub: PubSubService,
    private cloudFunctions: CloudFunctionsService,
    private toastCtrl: ToastController,
    private overviewData: BodyCompOverviewDataService,
    private settings: SettingsService,
    private trainingData: TrainingOverviewDataService,
    private trainingActions: TrainingOverviewActionsService,
    private navCtrl: NavController,
  ) { }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  /**
   * Whether the coach has typed a calorie target that leaving would throw away. Asked by
   * the page's leave guard, which owns the dialog. The rule itself lives in
   * `weekly-checkup-coach-resolution.ts`, where a unit test can reach it.
   */
  hasUnsavedCalorieTarget(): boolean {
    const isFieldOpen = this.coachingDashboardOn ? this.decision === CoachResolvedAction.Modified : this.reviewMode === 'modify';
    return hasUnsavedCoachCalorieTarget(isFieldOpen, this.customCalories, this.recommendedTargetDisplay());
  }

  // the daily calorie target in force before any adjustment
  protected currentTargetDisplay(): number {
    return Math.round(this.checkup?.calorieIntakeBaseline || 0);
  }
  // the daily calorie target the app recommends
  protected recommendedTargetDisplay(): number {
    return Math.round(this.checkup?.recommendedAvgCalories || 0);
  }
  // the difference between the two, signed, shown next to the recommendation
  protected recommendedDeltaDisplay(): TranslatedString {
    const cur = this.currentTargetDisplay();
    const next = this.recommendedTargetDisplay();
    const diff = next - cur;
    const sign = diff >= 0 ? '+' : '';
    return LL.coach.checkup.kcalDelta({ sign, diff });
  }

  /** the coach picked a tab; the Body Composition tab loads its cards the first time it is opened */
  protected onTabPicked(tab: CoachReviewTab) {
    if (tab !== CoachReviewTab.Decision && tab !== CoachReviewTab.BodyComposition && tab !== CoachReviewTab.Training) return;
    this.tab = tab;
    if (tab === CoachReviewTab.BodyComposition && !this.overviewRequested) {
      void this.loadOverviewAsync();
    }
    if (tab === CoachReviewTab.Training && !this.trainingRequested) {
      void this.loadTrainingAsync();
    }
    this.cd.markForCheck();
  }

  /**
   * Load the client's Body Composition overview for this check-up: the same request the client's
   * own completed view makes, so both show the same cards. A failure shows a retry message in the
   * tab and is reported quietly; the Decision tab and its buttons keep working.
   */
  protected async loadOverviewAsync() {
    this.overviewRequested = true;
    this.overviewLoading = true;
    this.overviewFailed = false;
    this.cd.markForCheck();
    try {
      const model = await this.overviewData.loadAsync(
        completedCheckupOverviewRequest(this.checkup, this.debug.getNowLocal()), this.docWatcher);
      // if the coach resolved the check-up and the page closed meanwhile, there is nothing to draw on
      if (this.destroyGuard.isDestroyed) return;
      this.overviewModel = model;
    } catch (err) {
      if (this.destroyGuard.isDestroyed) return;
      this.overviewFailed = true;
      this.logging.trackSilentProblem('coachReviewOverviewLoadFailed', { error: describeError(err) });
    } finally {
      if (!this.destroyGuard.isDestroyed) {
        this.overviewLoading = false;
        this.cd.markForCheck();
      }
    }
  }

  /**
   * Load the client's Training Overview for the Training tab: the newest 4 sessions of each workout,
   * up to today, or the page each card was paged to. A failure shows a retry message in the tab and is reported quietly; the Decision
   * tab and its buttons keep working.
   */
  protected async loadTrainingAsync() {
    this.trainingRequested = true;
    const loadNumber = ++this.trainingLoadCounter;
    this.trainingLoading = true;
    this.trainingFailed = false;
    this.cd.markForCheck();
    try {
      const result = await this.trainingData.loadAsync(this.docWatcher, this.trainingPaging.windowDays, this.trainingPaging.windowFor);
      // if a newer load started, or the page closed meanwhile, this answer is stale
      if (this.destroyGuard.isDestroyed || loadNumber !== this.trainingLoadCounter) return;
      const model = await this.trainingPaging.acceptLoadAsync(result);
      if (this.destroyGuard.isDestroyed || loadNumber !== this.trainingLoadCounter) return;
      this.trainingModel = model;
      this.trainingButtons = this.trainingPaging.buttonsFor(model);
    } catch (err) {
      if (this.destroyGuard.isDestroyed || loadNumber !== this.trainingLoadCounter) return;
      this.trainingFailed = true;
      this.logging.trackSilentProblem('coachReviewTrainingLoadFailed', { error: describeError(err) });
    } finally {
      if (!this.destroyGuard.isDestroyed && loadNumber === this.trainingLoadCounter) {
        this.trainingLoading = false;
        this.cd.markForCheck();
      }
    }
  }

  /**
   * Load the Training tab again, if it has been opened. The page calls this when a page pushed
   * from here closes (Exercise Settings, Edit Workouts, the Training Overview), because any of
   * them can change the program the tab shows.
   */
  async reloadTrainingAsync(): Promise<void> {
    if (!this.trainingRequested || this.destroyGuard.isDestroyed) return;
    await this.loadTrainingAsync();
  }

  /** "Show Earlier" and "Show Later" on a workout card of the Training tab, as on the Training Overview page */
  protected onTrainingShowEarlier(sectionIndex: number) {
    void this.pageTrainingAsync(model => this.trainingPaging.showEarlierAsync(model, sectionIndex));
  }

  protected onTrainingShowLater(sectionIndex: number) {
    void this.pageTrainingAsync(model => this.trainingPaging.showLaterAsync(model, sectionIndex));
  }

  /** Applies one press: a re-slice of the loaded logs, or a load of more when the press asked for older logs. A press during a load is ignored. */
  private async pageTrainingAsync(press: (model: TrainingOverviewModel) => Promise<TrainingOverviewPagingAnswer>) {
    if (!this.trainingModel || this.trainingLoading) return;
    const loadNumber = ++this.trainingLoadCounter;
    try {
      const answer = await press(this.trainingModel);
      if (this.destroyGuard.isDestroyed || loadNumber !== this.trainingLoadCounter) return;
      if (answer.needsReload) {
        void this.loadTrainingAsync();
        return;
      }
      this.trainingModel = answer.model ?? this.trainingModel;
      this.trainingButtons = this.trainingPaging.buttonsFor(this.trainingModel);
    } catch (err) {
      if (this.destroyGuard.isDestroyed || loadNumber !== this.trainingLoadCounter) return;
      this.trainingFailed = true;
      this.logging.trackSilentProblem('coachReviewTrainingLoadFailed', { error: describeError(err) });
    }
    this.cd.markForCheck();
  }

  /** a tap on an exercise of the Training tab: the same menu the Training Overview page opens */
  protected onTrainingRowTap(tap: TrainingOverviewRowTap) {
    void this.trainingActions.openRowMenuAsync(tap.row, tap.event, this.trainingSettingsCallback, () => void this.reloadTrainingAsync());
  }

  protected onOpenTrainingOverviewClick(event: Event) {
    // the tap must not reach the page behind the button, as with this screen's other buttons
    event.stopPropagation();
    void this.navCtrl.pushAsync(ITrainingOverviewPage, {});
  }

  /**
   * The weight change the check-up stored, in the coach's weight unit, drawn as a measurement row
   * (#6676): "+0.3 kg/wk (+0.4%)" with an arrow, "+0.4% of body weight gained per week" under it.
   * Undefined for a check-up that stored no weight trend.
   */
  protected get weightChange(): OverviewMeasurement | undefined {
    const snapshot = readProgressSnapshot(this.checkup);
    const unit = getMeasureUnitConversion('bodyweight_kg', this.settings.locale, 'kg');
    return weightChangeMeasurement(snapshot?.weightSlope_kgwk, snapshot?.estimatedBW_kg, unit);
  }

  /** the lowest daily target the app would set for this client, when the check-up stored it */
  protected minimumDailyIntake(): number | undefined {
    return readPositiveCalories(this.checkup?.minimumDailyIntake);
  }

  /** where the Decrease / Increase switch starts when the opening number equals the reported intake */
  protected defaultChangeDirection(): CalorieChangeDirection {
    return this.checkup?.isWeightGainGoal ? CalorieChangeDirection.Increase : CalorieChangeDirection.Decrease;
  }

  /** the row's title, for example "Accept the App's Target" */
  protected decisionLabel(choice: CoachResolvedAction): TranslatedString {
    switch (choice) {
      case CoachResolvedAction.Accepted: return LL.coach.checkup.decisionChoice.accept();
      case CoachResolvedAction.Modified: return LL.coach.checkup.decisionChoice.modify();
      case CoachResolvedAction.Declined: return LL.coach.checkup.decisionChoice.decline();
    }
  }

  /** the row's explanation line */
  protected decisionHelp(choice: CoachResolvedAction): TranslatedString {
    switch (choice) {
      case CoachResolvedAction.Accepted: return LL.coach.checkup.decisionChoice.acceptHelp();
      case CoachResolvedAction.Modified: return LL.coach.checkup.decisionChoice.modifyHelp();
      case CoachResolvedAction.Declined: return LL.coach.checkup.decisionChoice.declineHelp();
    }
  }

  /** the daily target shown at a row's right: only Accept has one, because the check-up does not store the target in force */
  protected decisionKcal(choice: CoachResolvedAction): TranslatedString | undefined {
    return choice === CoachResolvedAction.Accepted
      ? LL.coach.checkup.kcalValue({ value: this.recommendedTargetDisplay() })
      : undefined;
  }

  /** the coach picked an answer; Set a Different Target opens on the app's number, so the coach edits rather than types */
  protected onDecisionPicked(choice: CoachResolvedAction) {
    if (this.isInFlight) return;
    this.decision = choice;
    if (choice === CoachResolvedAction.Modified) {
      this.customCalories = this.recommendedTargetDisplay();
      // put the cursor in the field, once the template has actually drawn it
      this.destroyGuard.safeTimeout(() => {
        void (async () => {
          if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
          await this.calorieChangeInput?.focus();
        })();
      }, 0);
    }
    this.cd.markForCheck();
  }

  /** whether the picked answer can be sent: one is picked, and a different target has a number above zero */
  private hasValidDecision(): boolean {
    if (this.decision === undefined) return false;
    return this.decision !== CoachResolvedAction.Modified || (!!this.customCalories && this.customCalories > 0);
  }

  /** Confirm is off on the Decision tab until a valid answer is picked; on the other tabs it brings that tab forward */
  protected isConfirmDisabled(): boolean {
    return this.isInFlight || (this.tab === CoachReviewTab.Decision && !this.hasValidDecision());
  }

  /** Confirm sends the picked answer; with no valid answer picked it brings the Decision tab forward instead */
  onConfirmClick(event: Event) {
    if (!this.hasValidDecision()) {
      event.stopPropagation();
      this.tab = CoachReviewTab.Decision;
      this.cd.markForCheck();
      return;
    }
    lockClick(event, this.resolveAsync(this.decision));
  }

  /** the Modify button, shown only while the switch is off: opens the single kcal field */
  onModifyClick(event: Event) {
    event.stopPropagation();
    if (this.isInFlight) return;
    this.reviewMode = 'modify';
    // open the field holding the app's recommendation, so the coach edits a number rather
    // than typing one from nothing
    this.customCalories = this.recommendedTargetDisplay();
    this.cd.markForCheck();
    // put the cursor in the field, once the template has actually drawn it
    this.destroyGuard.safeTimeout(() => {
      void (async () => {
        if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
        await this.modifyInput?.focus();
      })();
    }, 0);
  }

  onCancelModifyClick(event: Event) {
    event.stopPropagation();
    if (this.isInFlight) return;
    this.reviewMode = 'summary';
    this.cd.markForCheck();
  }

  onAcceptClick(event: Event) {
    lockClick(event, this.resolveAsync(CoachResolvedAction.Accepted));
  }
  onApplyModifyClick(event: Event) {
    lockClick(event, this.resolveAsync(CoachResolvedAction.Modified));
  }
  onDeclineClick(event: Event) {
    lockClick(event, this.resolveAsync(CoachResolvedAction.Declined));
  }

  // one coach answer, start to finish. the check-up document is written to its final state
  // BEFORE the client's plan is touched, which is what makes a retry safe: a failure part
  // way through leaves the coach free to press the button again without the plan being
  // applied twice.
  private async resolveAsync(resolution: CoachResolvedAction) {
    if (this.isInFlight) return;
    this.isInFlight = true;

    const loader = this.loadingCtrl.create({
      content: LL.weeklyCheckup.loading.finishingCheckup(),
    });
    try {
      await loader.present();

      // check what we were given before changing anything. these failures are ones the coach
      // is meant to see — the ordinary cause is the check-up changing underneath them — so
      // they are thrown as their own type and end in a toast rather than in error telemetry
      const pendingPlan = (resolution !== CoachResolvedAction.Declined) ? this.getPendingPlan() : undefined;
      if (resolution !== CoachResolvedAction.Declined && !pendingPlan) {
        throw new CoachReviewValidationError('coach-review: pendingNewPlan missing');
      }
      if (resolution === CoachResolvedAction.Modified && (!this.customCalories || this.customCalories <= 0)) {
        throw new CoachReviewValidationError('coach-review: custom calorie target missing');
      }

      // for a modified target, scale the per-meal numbers here rather than re-running the
      // solver — the solver would read the override as the client's current intake and
      // adjust it again instead of honouring it
      if (resolution === CoachResolvedAction.Modified && pendingPlan) {
        applyCustomCaloriesToPendingPlan(pendingPlan, this.customCalories);
      }

      const outcome = getCoachResolutionOutcome(resolution, pendingPlan);
      const { newCalorieTarget } = outcome;

      // finish the check-up document FIRST, before the plan is touched (see the note above
      // this method). once it is written, a retry cannot re-enter this path with a stale
      // pending plan still attached.
      applyCoachResolutionToCheckup(this.checkup, resolution, outcome,
        this.debug.getNowLocal().toUTC().toISO() ?? new Date().toISOString());
      await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), this.checkup);

      // accepting and modifying both put the client on the new plan; declining does not
      if (pendingPlan) {
        await this.applyPendingPlanAsync(pendingPlan);
      }

      this.telemetry.trackEvent('weekly_checkup_coach_resolved', {
        resolution,
        modified_calories: newCalorieTarget ?? null,
      });

      // tell the client what their coach decided. a failure here does not undo anything —
      // the answer stands whether or not the message arrives.
      await this.notifyClientOfResolutionAsync(resolution, newCalorieTarget);

      // the answer is written; the page can close without asking about unsaved changes
      this.resolved.emit();
    } catch (err) {
      this.isInFlight = false;
      if (this.destroyGuard.isDestroyed) return;
      this.cd.markForCheck();
      if (err instanceof CoachReviewValidationError) {
        // a failure the coach is meant to see, kept apart from unexpected ones so that
        // ordinary state drift — a missing pending plan after somebody else resolved the
        // same check-up — does not fill error telemetry. still logged, so it can be traced.
        this.logging.trackSilentProblem('coachReviewValidation', { message: err.message });
        this.toastCtrl.showToast({ message: LL.coach.checkup.resolveError(), duration: 3500 });
      } else {
        this.logging.trackAbnormalException(err, untranslatedString('weekly checkup coach-resolve failed'));
      }
    } finally {
      await loader.dismiss().catch(() => {});
    }
  }

  // put the (possibly modified) pending plan and the body-fat reading that came with it onto
  // the client's live plan and fitness state.
  private async applyPendingPlanAsync(pendingPlan: FitnessPlan): Promise<void> {
    const pendingFitnessState = this.getPendingFitnessState();

    // keep a copy of the plan being replaced, the same way the client's own accept path does
    const fitnessPlanBackup = await this.userDb.createCurrentPlanBackupLog(DatabaseWrapper.getTransactionId());
    this.checkup.fitnessPlanBackupId = fitnessPlanBackup._id;
    await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), this.checkup);

    // only the body-fat fields are taken from the snapshot the client submitted. the rest of
    // the fitness state may have moved on since — a weigh-in between submitting and
    // resolving — and putting the old snapshot back would throw that away.
    if (pendingFitnessState?.bfData) {
      const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
      fitnessState.bfData = pendingFitnessState.bfData;
      if (pendingFitnessState.lastBfDataUpdate_utc) {
        fitnessState.lastBfDataUpdate_utc = pendingFitnessState.lastBfDataUpdate_utc;
      }
      if (pendingFitnessState.lastBfDataWeight_kg !== undefined) {
        fitnessState.lastBfDataWeight_kg = pendingFitnessState.lastBfDataWeight_kg;
      }
      await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
    }

    // apply the plan. the same care as above: the training program may have moved on since
    // the client submitted — a workout, an Edit Workouts change — so it is kept from the
    // live plan, and only the rest comes from the submitted one (#6500).
    const currentPlan = await this.userDb.loadPlanAsync(this.docWatcher);
    keepLiveTrainingProgram(pendingPlan, currentPlan);
    deepOverwriteCouchSafe(currentPlan, pendingPlan);
    await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());

    await this.userDb.clearBacklogCacheAsync();
    this.pubSub.publish(BodyCompositionDataChangedEvent, {});
  }

  // tell the client their coach has answered. a delivery failure is logged and otherwise
  // ignored — the plan change is already saved, and the client's home screen shows it.
  private async notifyClientOfResolutionAsync(resolution: CoachResolvedAction, newCalorieTarget: number | undefined): Promise<void> {
    const managingClient = this.userDb.getCoachManagingClient();
    if (!managingClient?.clientUid) return;
    const notifyParams: ClientNotifyCheckupResolvedParams = {
      type: 'checkupResolved',
      clientUid: managingClient.clientUid,
      checkupLogId: this.checkup._id,
      resolution,
      newCalorieTarget,
      coachDisplayName: this.auth.user?.name,
    };
    try {
      const result = await this.cloudFunctions.coachNotifyClientAsync(notifyParams);
      if (result.attempted && !result.emailSent) {
        this.logging.trackSilentProblem('coachNotifyClientEmailFailed', { type: 'checkupResolved', emailError: result.emailError });
      }
    } catch (err) {
      this.logging.trackSilentProblem('coachNotifyClientFailed', { type: 'checkupResolved', err: describeErrorForLog(err) });
    }
  }

  // read back the plan the client's app worked out and stored when they submitted
  private getPendingPlan(): FitnessPlan | undefined {
    if (!this.checkup?.pendingNewPlan) return undefined;
    try {
      return JSON.parse(this.checkup.pendingNewPlan) as FitnessPlan;
    } catch (err) {
      this.logging.trackAbnormalException(err, untranslatedString('weekly-checkup: failed to parse pendingNewPlan'));
      return undefined;
    }
  }

  // read back the body-fat reading and derived values captured when the client submitted
  private getPendingFitnessState(): FitnessState | undefined {
    if (!this.checkup?.pendingNewFitnessState) return undefined;
    try {
      return JSON.parse(this.checkup.pendingNewFitnessState) as FitnessState;
    } catch (err) {
      this.logging.trackAbnormalException(err, untranslatedString('weekly-checkup: failed to parse pendingNewFitnessState'));
      return undefined;
    }
  }
}
